import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { fromPairs, groupBy, sortBy, times, toPairs } from 'lodash'
import { Padding } from 'web-makeup/dist/design-system/styles/spacings'
import Skeleton from '@yisheng90/react-loading'
import { colors } from 'web-makeup/dist/design-system/config/data/theme'
import { isAdmin, isProducer, isManager } from 'web-fns/dist/auth'
import { useFlags } from 'launchdarkly-react-client-sdk'
import Switch from 'web-makeup/dist/Switch'

import BrandOrderCard from '../../components/BrandOrderCard/BrandOrderCard'
import DefaultButton from '../../components/DefaultButton/DefaultButton'

import { client as apolloClient } from '../../../ApolloProvider/ApolloProvider'
import EnvironmentContext from '../../../EnvironmentContext'
import { ProjectLevel } from '../../../../typings/types'
import { GET_ALL_PROJECTS } from '../../../../queries/GetAllProjectsv2'
import { validateProjects } from '../../../../helpers/allProjectsPaginatedDataHelper'
import PurchaseRedesigned from '../../../../components/PurchaseRedesigned/PurchaseRedesigned'
import { useProductize } from '../../../../hooks/useProductize'
import { getDashboardId } from '../../../../helpers/getDashboardId'

import {
  OrdersHeader,
  BrandOrderCards,
  SectonTitle,
  ShowHiddenLabel,
  LoadingCardsWrapper,
  NavbarStyles,
  StyledCardNew,
  NewBriefBanner,
  BreadCrumbs,
  ClearBoth,
} from '../BrandDashboardTemplates.style'

interface BrandOrdersProps {
  brandOrderUuid?: string
}

export const BrandOrders: React.FC<BrandOrdersProps> = ({ brandOrderUuid }) => {
  const [refetchServices, setRefetchServices] = React.useState(false)
  const [refetchSolutions, setRefetchSolutions] = React.useState(false)
  const [showHidden, setShowHidden] = React.useState(false)
  const [isCreatePageVisible, setIsCreatePageVisible] = React.useState(false)
  const [currentBrandServices, setCurrentBrandServices] = React.useState<any[]>([])
  const [currentBrandSolutions, setCurrentBrandSolutions] = React.useState<any[]>([])

  const { BRAND_BASE_URL, ORDER_APP } = React.useContext(EnvironmentContext)
  const { getProducts: getServices } = useProductize(
    BRAND_BASE_URL,
    brandOrderUuid,
    () => setRefetchServices(true),
    'services'
  )
  const { getProducts: getSolutions } = useProductize(
    BRAND_BASE_URL,
    brandOrderUuid,
    () => setRefetchSolutions(true),
    'solutions'
  )

  React.useEffect(() => {
    document.body.className = ''
    document.body.classList.add('new-order')
    return () => {
      document.body.className = ''
      document.body.classList.add('new-order')
    }
  }, [])

  const flags = useFlags()
  const enablePreferences = flags['brand-dashboard-preferences']
  const enableProductize = flags['brand-dashboard-productize']

  React.useEffect(
    () => {
      if (brandOrderUuid !== undefined) {
        getServices().then(services => {
          if (services) {
            setCurrentBrandServices(services)
            setRefetchServices(false)
          }
        })
      }
    },
    [brandOrderUuid, refetchServices]
  )

  React.useEffect(
    () => {
      if (brandOrderUuid !== undefined) {
        getSolutions().then(solutions => {
          if (solutions) {
            setCurrentBrandSolutions(solutions)
            setRefetchSolutions(false)
          }
        })
      }
    },
    [brandOrderUuid, refetchSolutions]
  )

  const servicesFilterFunction = (
    data: any[],
    options: {
      isFavorite?: boolean
      showHidden?: boolean
      isPrivate?: boolean
      enablePreferences?: boolean
    }
  ) => {
    return (
      data &&
      Array.isArray(data) &&
      data
        .filter(ser => {
          // Filter by 'isFavorite'.
          if (options.isFavorite === undefined) {
            // If there is no attempt to filter favorites, return everything.
            return true
          }
          if (!enablePreferences) {
            // If we're trying to filter by favorite with preferences disabled, return the
            // opposite of what is asked for. Ie. if asked to show favorites, return nothing.
            // If asked to show non-favorites, return everything.
            return !options.isFavorite
          }

          // Preferences is enabled - respect the isFavorite flag.
          return options.isFavorite ? ser.isFavorite : !ser.isFavorite
        })
        .filter(ser => {
          // Filter by 'showHidden'.
          // If the preferences flag is turned off, don't hide anything.
          if (!enablePreferences) {
            return true
          }

          return options.showHidden === false ? !ser.isHidden : true
        })
        .filter(ser => {
          // Filter by 'isPrivate'.
          // The original logic doesn't handle the case where we want to show
          // both pubic and non-public 'favorite' items in the featured area
          // together. This handles that edge case:
          if (enablePreferences && options.isFavorite) {
            return options.isPrivate === false ? ser.isPublic : true
          }

          // Original logic, pre-preferences.
          return options.isPrivate ? !ser.isPublic : ser.isPublic
        })
    )
  }
  const featuredServices = servicesFilterFunction(currentBrandServices, {
    isFavorite: true,
    showHidden,
    enablePreferences,
  })
  const featuredSolutions = servicesFilterFunction(currentBrandSolutions, {
    isFavorite: true,
    showHidden,
    enablePreferences,
  })
  const publicServices = servicesFilterFunction(currentBrandServices, {
    isFavorite: false,
    isPrivate: false,
    showHidden,
    enablePreferences,
  })
  const publicSolutions = servicesFilterFunction(currentBrandSolutions, {
    isFavorite: false,
    isPrivate: false,
    showHidden,
    enablePreferences,
  })
  const privateServices = servicesFilterFunction(currentBrandServices, {
    isFavorite: false,
    isPrivate: true,
    showHidden,
    enablePreferences,
  })
  const privateSolutions = servicesFilterFunction(currentBrandSolutions, {
    isFavorite: false,
    isPrivate: true,
    showHidden,
    enablePreferences,
  })

  // The order of the 'grouped' keys ('Plan' / 'Shoot' / 'Edit' etc) changes randomly when you start hiding
  // services at the beginning of a group. To resolve this, we first get the 'natural' keyOrder of the raw
  // service list (including hidden), and make sure that the 'grouped' + filtered key order matches that.
  const groupedServices = React.useMemo(
    () => {
      // const keyOrder =
      //   Array.isArray(currentBrandServices) &&
      //   Array.from(new Set(((currentBrandServices || []) as any).map(service => service.type)))

      // This is custom type orders requested in task CORE-900
      const customOrder = ['Plan', 'Shoot', 'Edit', 'Concierge', 'Digital Assets']
      const unordered = publicServices ? groupBy(publicServices, service => service.type) : {}
      return fromPairs(
        sortBy(toPairs(unordered), ([key]) => customOrder && customOrder.indexOf(key))
      )
    },
    [publicServices]
  )

  const currentBrandPrivateInventory = privateServices &&
    privateSolutions &&
    Array.isArray(privateServices) &&
    Array.isArray(privateSolutions) && [...privateServices, ...privateSolutions]

  const isAdminUser = isAdmin() || isProducer() || isManager()

  const currentBrandId = getDashboardId()
  const {
    data: templatesProjectsData,
    loading: templatesProjectsDataLoading,
    refetch: refreshTemplates,
  } = useQuery(GET_ALL_PROJECTS, {
    variables: {
      brandUuid: currentBrandId,
      pageSize: 1000,
      projectLevels: [ProjectLevel.TEMPLATES],
      orderDirection: 'DESC',
    },
    client: apolloClient,
    errorPolicy: 'all',
  })

  const templatesProjects = validateProjects(templatesProjectsData)

  const ProjectCardSkeletonNew = (
    <StyledCardNew data-testid="project_card__skeleton">
      <Padding top bottom left right size="mSmall">
        <Skeleton width="100%" height={164} />
        <Padding bottom size="small" />
        <Skeleton width="60%" height={36} />
        <Padding bottom size="small" />
        <Skeleton width="50%" height={36} />
      </Padding>
    </StyledCardNew>
  )
  const PageLoadingTabs = () => {
    return (
      <LoadingCardsWrapper>
        {times(12, idx => {
          return <div key={`skeleton-${idx}`}>{ProjectCardSkeletonNew}</div>
        })}
      </LoadingCardsWrapper>
    )
  }

  const breadcrumbIcon = (
    <svg width="8" height="16" viewBox="0 0 8 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.146447 0.646447C0.341709 0.451184 0.658291 0.451184 0.853553 0.646447L7.85355 7.64645C7.94732 7.74021 8 7.86739 8 8C8 8.13261 7.94732 8.25979 7.85355 8.35355L0.853553 15.3536C0.658291 15.5488 0.341709 15.5488 0.146447 15.3536C-0.0488155 15.1583 -0.0488155 14.8417 0.146447 14.6464L6.79289 8L0.146447 1.35355C-0.0488155 1.15829 -0.0488155 0.841709 0.146447 0.646447Z"
        fill="#9E9E9E"
      />
    </svg>
  )
  const mySolutionsTitle = <SectonTitle data-testid="my-solutions-title">My Solutions</SectonTitle>
  const currentBuild = ''
  return (
    <>
      <NavbarStyles />

      {enablePreferences ? (
        <>
          <ShowHiddenLabel>
            Show hidden products{' '}
            <Switch checked={showHidden} onChange={() => setShowHidden(!showHidden)} />
          </ShowHiddenLabel>
          {templatesProjects && templatesProjects.length === 0 && <ClearBoth />}
        </>
      ) : null}
      {isCreatePageVisible ? (
        <>
          <BreadCrumbs isCreateView={isCreatePageVisible}>
            <span onClick={() => setIsCreatePageVisible(false)}>New Order</span>
            {breadcrumbIcon}
            <span onClick={() => setIsCreatePageVisible(true)}>Submit a video brief</span>
          </BreadCrumbs>
          <PurchaseRedesigned />
        </>
      ) : (
        <>
          <OrdersHeader>
            <h3 data-testid="header-title">New Order</h3>
          </OrdersHeader>

          {enablePreferences && featuredServices && featuredServices.length > 0 ? (
            <>
              <SectonTitle id="featuredServicesTitle" data-testid="featured-services-title">
                Featured Services
              </SectonTitle>
              <BrandOrderCards data-testid="featured-services-cards">
                {featuredServices.map(prod => {
                  return (
                    <BrandOrderCard
                      brandOrderUuid={brandOrderUuid}
                      build={currentBuild}
                      isSolution={false}
                      key={`featured-services-${prod.id}`}
                      product
                      productize
                      project={prod}
                      refetch={() => setRefetchServices(true)}
                    />
                  )
                })}
              </BrandOrderCards>
            </>
          ) : null}

          {enablePreferences && featuredSolutions && featuredSolutions.length > 0 ? (
            <>
              <SectonTitle id="featuredSolutionsTitle" data-testid="featured-solutions-title">
                Featured Solutions
              </SectonTitle>
              <BrandOrderCards data-testid="featured-solutions-cards">
                {featuredSolutions.map(prod => {
                  return (
                    <BrandOrderCard
                      brandOrderUuid={brandOrderUuid}
                      build={currentBuild}
                      isSolution={true}
                      key={`featured-solutions-${prod.id}`}
                      product
                      productize
                      project={prod}
                      refetch={() => setRefetchSolutions(true)}
                    />
                  )
                })}
              </BrandOrderCards>
            </>
          ) : null}

          {templatesProjectsDataLoading && <>{PageLoadingTabs()}</>}
          {!templatesProjectsDataLoading &&
            templatesProjects &&
            templatesProjects.length > 0 && (
              <>
                <SectonTitle id="templatesTitle" data-testid="templates-title">
                  Order Again
                </SectonTitle>
                <BrandOrderCards data-testid="templates-cards">
                  {templatesProjects.map(prod => {
                    return (
                      <BrandOrderCard
                        project={prod}
                        key={`template-${prod.uuid}`}
                        template
                        refetch={refreshTemplates}
                      />
                    )
                  })}
                </BrandOrderCards>
              </>
            )}
          <NewBriefBanner>
            <div>
              <h3>Submit a new brief</h3>
              <p>
                Would you like to discuss your video project with us? Simply provide a brief, and
                we’ll handle it for you.
              </p>
            </div>
            <DefaultButton
              fontSize="1rem"
              padding="0 1.5rem"
              backgroundColor={colors.white}
              color="#1936D0"
              buttonHeight="3.375rem"
              rounded
              hoverBackground={colors.white}
              hoverColor="#1936D0"
              style={{ whiteSpace: 'nowrap' }}
              isExternal
              href={`${ORDER_APP}/solutions/customisable-solution?selectedBrandUuid=${brandOrderUuid}&brandId=${brandOrderUuid}`}
            >
              Submit a brief
            </DefaultButton>
          </NewBriefBanner>

          {(isAdminUser && !enableProductize) ||
            (enableProductize && (
              <>
                {!enableProductize && (
                  <NewBriefBanner className="productize">
                    <div>
                      <img
                        src="https://res.cloudinary.com/ninety-seconds/image/upload/v1699984915/hide-icon-white.svg"
                        alt="hide-icon"
                      />
                      <h3>New Productize Inventory</h3>
                      <p>
                        The inventory below is pulled from the new Productize BE and is currently
                        only visible to Producers, Managers and Admin.
                      </p>
                    </div>
                  </NewBriefBanner>
                )}
                {currentBrandPrivateInventory &&
                  currentBrandPrivateInventory.length !== 0 && (
                    <>
                      {mySolutionsTitle}
                      <BrandOrderCards data-testid="my-solutions-cards">
                        {currentBrandPrivateInventory.map(prod => {
                          const isSolution = prod.type === 'solution' || prod.type === undefined
                          const refetch = isSolution ? setRefetchSolutions : setRefetchServices
                          return (
                            <BrandOrderCard
                              brandOrderUuid={brandOrderUuid}
                              build={currentBuild}
                              isSolution={isSolution}
                              key={prod.uuid || prod.id}
                              product
                              productize
                              project={prod}
                              refetch={() => refetch(true)}
                            />
                          )
                        })}
                      </BrandOrderCards>
                    </>
                  )}
                {publicServices &&
                  publicServices.length !== 0 && (
                    <>
                      {Object.keys(groupedServices).map((keyName, e) => (
                        <React.Fragment key={`grouped-service-${e}`}>
                          {groupedServices[keyName].length !== 0 && (
                            <SectonTitle
                              key={`grouped-service-title-${e}`}
                              data-testid="solutions-title"
                            >
                              {keyName.toString()}
                            </SectonTitle>
                          )}
                          <BrandOrderCards data-testid="solutions-cards">
                            {groupedServices[keyName] &&
                              groupedServices[keyName].map((prod: any) => {
                                if (prod.isLive) {
                                  return (
                                    <BrandOrderCard
                                      brandOrderUuid={brandOrderUuid}
                                      build={currentBuild}
                                      key={`grouped-service-item-${prod.id}`}
                                      product
                                      productize
                                      project={prod}
                                      refetch={() => setRefetchServices(true)}
                                    />
                                  )
                                }
                                return
                              })}
                          </BrandOrderCards>
                        </React.Fragment>
                      ))}
                    </>
                  )}
                {publicSolutions &&
                  publicSolutions.length !== 0 && (
                    <>
                      <SectonTitle data-testid="solutions-title">Solutions</SectonTitle>
                      <BrandOrderCards data-testid="solutions-cards">
                        {Array.isArray(publicSolutions) &&
                          publicSolutions.map((solution: any) => {
                            if (solution.isLive) {
                              return (
                                <BrandOrderCard
                                  brandOrderUuid={brandOrderUuid}
                                  build={currentBuild}
                                  isSolution
                                  key={`public-solutions-${solution.uuid}`}
                                  product
                                  productize
                                  project={solution}
                                  refetch={() => setRefetchSolutions(true)}
                                />
                              )
                            }
                            return
                          })}
                      </BrandOrderCards>
                    </>
                  )}
              </>
            ))}
        </>
      )}
    </>
  )
}



// WEBPACK FOOTER //
// ./src/components/BrandDashboard/BrandDashboardTemplates/BrandOrders/BrandOrders.tsx